import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { recolecciondom } from '../../servicios/recoleccion-dom.service';
import jwt_decode from "jwt-decode";
import Swiper, { Autoplay, EffectFade } from 'swiper';

Swiper.use([Autoplay, EffectFade]);

interface Package {
  name: string;
  quantity: number;
  priceUnit: number;
  totalPrice: number;
  isPopular?: boolean;
  savings?: string;
  sku: string;
  weightDesc?: string;
}

type TabId = '1kg' | '5kg' | 'heavy';

@Component({
  selector: 'app-landing-guias-web',
  templateUrl: './landing-guias-web.component.html',
  styleUrls: ['./landing-guias-web.component.css']
})
export class LandingGuiasWebComponent implements OnInit, AfterViewInit, OnDestroy {
  activeTab: TabId = '1kg';
  heavyTabLabel = '🚛 Carga Pesada (+10kg)';
  
  // Arreglo total consolidado para el Swiper (los 9 paquetes)
  allPackages: Package[] = [];

  // Data temporal por categoría
  data1kg: Package[] = [];
  data5kg: Package[] = [];
  dataHeavy: Package[] = [];

  // Instancia de Swiper
  swiper: any;
  swiperReady: boolean = false;

  // Modal logic
  isModalOpen: boolean = false;
  selectedPkg: Package | null = null;

  constructor(
    private route: ActivatedRoute,
    private breakpointObserver: BreakpointObserver,
    private recolecciondom: recolecciondom,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);

    this.route.fragment.subscribe(f => {
      if (f) {
        setTimeout(() => {
          this.scrollTo(f);
        }, 300);
      }
    });

    this.breakpointObserver
      .observe(['(max-width: 719px)'])
      .subscribe(r => {
        this.heavyTabLabel = r.matches ? '🚛 Carga +10kg' : '🚛 Carga Pesada (+10kg)';
      });

    this.loadDynamicPackages();
  }

  ngAfterViewInit(): void {
    // Si la data llego super rapido, lo inicializamos, o se inicializara en loadDynamicPackages
  }

  ngOnDestroy(): void {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }

  initSwiper() {
    this.swiperReady = true;
    this.cdr.detectChanges(); // forzamos render para asegurar elementos en el DOM

    this.swiper = new Swiper('.swiper-pricing-hero', {
      slidesPerView: 1,
      spaceBetween: 30,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      on: {
        slideChange: () => {
          this.handleSlideChange();
        }
      }
    });
  }

  handleSlideChange() {
    if (!this.swiper) return;
    const index = this.swiper.realIndex; // Importante para loops

    const limit1 = this.data1kg.length;
    const limit2 = limit1 + this.data5kg.length;
    const limit3 = limit2 + this.dataHeavy.length;

    if (index >= 0 && index < limit1) {
      this.activeTab = '1kg';
    } else if (index >= limit1 && index < limit2) {
      this.activeTab = '5kg';
    } else if (index >= limit2 && index < limit3) {
      this.activeTab = 'heavy';
    }
    this.cdr.detectChanges();
  }

  setTab(tab: TabId) {
    this.activeTab = tab;
    // Mover el swiper a la posicion correspondiente usando Loop
    if (!this.swiper) return;
    
    const limit1 = this.data1kg.length;
    const limit2 = limit1 + this.data5kg.length;

    if (tab === '1kg') {
      this.swiper.slideToLoop(0);
    } else if (tab === '5kg') {
      this.swiper.slideToLoop(limit1);
    } else if (tab === 'heavy') {
      this.swiper.slideToLoop(limit2);
    }
  }

  // --- Modal Logic ---
  openDetails(pkg: Package) {
    this.selectedPkg = pkg;
    this.isModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeDetails() {
    this.isModalOpen = false;
    document.body.style.overflow = 'auto';
  }
  // -------------------

  loadDynamicPackages() {
    let idcliente = -1;
    const token = sessionStorage.getItem("token") || localStorage.getItem("token");
    if (token) {
      try {
        const decoded: any = jwt_decode(token);
        idcliente = decoded.idUser || -1;
      } catch (e) { }
    }

    this.recolecciondom.getPaquetesGuias(idcliente).then((data: any[]) => {
      if (!data) return;

      this.data1kg = data
        .filter(p => p.descripcion1 === '1 Kg.' && [10, 25, 100].includes(Number(p.numGuias)))
        .map(p => this.mapToPackage(p, 'Pack 1KG'))
        .sort((a, b) => a.quantity - b.quantity);

      this.data5kg = data
        .filter(p => p.descripcion1 === '5 Kg.' && [10, 25, 100].includes(Number(p.numGuias)))
        .map(p => this.mapToPackage(p, 'Pack 5KG'))
        .sort((a, b) => a.quantity - b.quantity);

      this.dataHeavy = data
        .filter(p => {
          const n = parseInt(String(p.descripcion1), 10);
          // Mostrar SÓLO los 3 paquetes de la categoría de 10 kg
          return !isNaN(n) && n === 10 && [10, 20, 30].includes(Number(p.numGuias));
        })
        .map(p => this.mapToPackage(p, p.descripcion1.replace('.', '')))
        .sort((a, b) => a.quantity - b.quantity);

      // Destacar exclusivamente Crecimiento como popular
      const fullList = [...this.data1kg, ...this.data5kg, ...this.dataHeavy];
      fullList.forEach(p => {
        if (p.name.includes('Crecimiento') || p.quantity === 25) {
          p.isPopular = true;
        }
      });

      // Si por alguna razón faltan, hacemos fallback local
      if (this.data1kg.length === 0) {
        this.fillFallbackData();
      } else {
        this.allPackages = fullList;
      }
      
      // Iniciar el auto-slider en el siguiente ciclo
      setTimeout(() => {
        if (!this.swiper) this.initSwiper();
      }, 200);

    }).catch(err => {
      console.error("Error al cargar paquetes dinámicos", err);
      this.fillFallbackData();
      setTimeout(() => {
        if (!this.swiper) this.initSwiper();
      }, 200);
    });
  }

  private fillFallbackData() {
    this.data1kg = [
      { name: 'Emprendedor', quantity: 10, priceUnit: 229.90, totalPrice: 2299, savings: 'PARA EMPEZAR', sku: '1kg-10', weightDesc: '1 kg' },
      { name: 'Crecimiento', quantity: 25, priceUnit: 225.52, totalPrice: 5638, isPopular: true, savings: 'MÁS POPULAR', sku: '1kg-25', weightDesc: '1 kg' },
      { name: 'Alto Movimiento', quantity: 100, priceUnit: 207.90, totalPrice: 20790, savings: 'MAXIMO AHORRO', sku: '1kg-100', weightDesc: '1 kg' }
    ];
    this.data5kg = [
      { name: 'Emprendedor', quantity: 10, priceUnit: 295.90, totalPrice: 2959, savings: 'PARA EMPEZAR', sku: '5kg-10', weightDesc: '5 kg' },
      { name: 'Crecimiento', quantity: 25, priceUnit: 290.52, totalPrice: 7263, isPopular: true, savings: 'MÁS POPULAR', sku: '5kg-25', weightDesc: '5 kg' },
      { name: 'Alto Movimiento', quantity: 100, priceUnit: 269.50, totalPrice: 26950, savings: 'MAXIMO AHORRO', sku: '5kg-100', weightDesc: '5 kg' }
    ];
    this.dataHeavy = [
      { name: 'Emprendedor', quantity: 10, priceUnit: 317.90, totalPrice: 3179, savings: 'PARA EMPEZAR', sku: '10kg-10', weightDesc: '10 kg.' },
      { name: 'Crecimiento', quantity: 20, priceUnit: 306.90, totalPrice: 6138, isPopular: true, savings: 'MÁS POPULAR', sku: '10kg-20', weightDesc: '10 kg.' },
      { name: 'Empresarial', quantity: 30, priceUnit: 295.90, totalPrice: 8877, savings: 'RECOMENDADO', sku: '10kg-30', weightDesc: '10 kg.' }
    ];
    this.allPackages = [...this.data1kg, ...this.data5kg, ...this.dataHeavy];
  }

  private getBadgeText(name: string): string {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('emprendedor')) return 'PARA EMPEZAR';
    if (lowerName.includes('crecimiento')) return 'MÁS POPULAR';
    if (lowerName.includes('empresarial')) return 'RECOMENDADO';
    if (lowerName.includes('alto movimiento')) return 'MAXIMO AHORRO';
    if (lowerName.includes('elite')) return 'MÁXIMO VOLUMEN';
    return '';
  }

  private mapToPackage(p: any, defaultName: string): Package {
    const kg = parseInt(String(p.descripcion1), 10);
    const quantity = Number(p.numGuias);
    const totalPrice = Number(p.costo);
    const priceUnit = quantity > 0 ? totalPrice / quantity : 0;
    const name = p.nombre || defaultName;

    return {
      name: name,
      quantity: quantity,
      priceUnit: priceUnit,
      totalPrice: totalPrice,
      sku: `${kg}kg-${quantity}`,
      savings: p.promocion || this.getBadgeText(name),
      weightDesc: p.descripcion1.toLowerCase()
    };
  }

  scrollTo(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

}
